import { useCallback, useEffect, useMemo, useState } from 'react';
import { PlayerStatus, type PresenceSnapshot } from 'minka-shared';
import * as adminApi from '@/services/admin-api';
import { describeAdminError } from '@/utils/api-error';

const EMPTY: PresenceSnapshot = { users: [], islands: [] };

export interface PresenceEntry {
  roomId: string;
  status: PlayerStatus;
}

export interface UsePresenceResult {
  data: PresenceSnapshot;
  isLoading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  /** Where a user is standing right now, or undefined when they are offline. */
  userPresence: (userId: string) => PresenceEntry | undefined;
  /** How many people are standing on an island right now. */
  islandCount: (roomId: string) => number;
}

/**
 * One presence snapshot (§6.7). Loaded when the tab mounts and on the Refresh
 * button, never on a timer: the console is a tool a sole operator opens, and
 * polling every open tab for a number that is only ever glanced at is not worth
 * the traffic it would cost.
 *
 * @param enabled - whether the first snapshot may be taken yet. The console
 *   entered from in-game opens while the island is still being left, and
 *   presence is per-island: a snapshot taken then shows the operator standing
 *   where they just walked out of, until they press Refresh. So the first load
 *   waits for the exit to settle. Refresh never does — it is a deliberate ask.
 */
export function usePresence(enabled = true): UsePresenceResult {
  const [data, setData] = useState<PresenceSnapshot>(EMPTY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      setData(await adminApi.getPresence());
      setError(null);
    } catch (loadError) {
      setError(describeAdminError(loadError, 'Failed to load presence'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    void refresh();
  }, [enabled, refresh]);

  // The snapshot is a pair of flat lists; the table reads it a row at a time.
  const byUser = useMemo(() => {
    const index = new Map<string, PresenceEntry>();
    for (const entry of data.users) {
      index.set(entry.userId, { roomId: entry.roomId, status: entry.status ?? PlayerStatus.AVAILABLE });
    }
    return index;
  }, [data]);

  const byIsland = useMemo(() => {
    const index = new Map<string, number>();
    for (const island of data.islands) {
      index.set(island.roomId, island.count);
    }
    return index;
  }, [data]);

  const userPresence = useCallback((userId: string) => byUser.get(userId), [byUser]);
  const islandCount = useCallback((roomId: string) => byIsland.get(roomId) ?? 0, [byIsland]);

  return { data, isLoading, error, refresh, userPresence, islandCount };
}
